import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { viewReviewDetails } from '../../slice/reviewSlice'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'

const BOOKING_STATUS_BADGE = {
    ongoing: { label: 'Ongoing', cls: 'bg-primary' },
    completed: { label: 'Completed', cls: 'bg-success' },
    cancelled: { label: 'Cancelled', cls: 'bg-danger' },
    pending: { label: 'Pending', cls: 'bg-warning text-dark' },
}

const StarRating = ({ rating }) => (
    <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map(star => (
            <i
                key={star}
                className="fe-star"
                style={{
                    fontSize: '16px',
                    color: star <= rating ? '#ffc107' : '#dee2e6',
                    filter: star <= rating ? 'none' : 'opacity(0.4)',
                }}
            />
        ))}
        <span style={{ fontSize: '13px', color: '#6c757d', marginLeft: '6px', fontWeight: '600' }}>
            {rating} / 5
        </span>
    </div>
)

const ViewReviewDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const reviewId = location.state?.reviewId

    const { reviewDetail } = useSelector(state => state.review)

    useEffect(() => {
        if (!reviewId) {
            navigate('/reviews')
            return
        }
        dispatch(viewReviewDetails({ id: reviewId }))
    }, [reviewId, dispatch, navigate])

    const {
        id,
        rating,
        review,
        is_active,
        is_deleted,
        createdAt,
        updatedAt,
        tutorRating,
        parentRating,
        bookingR,
    } = reviewDetail

    const bookingBadge = BOOKING_STATUS_BADGE[bookingR?.status] || { label: bookingR?.status || 'NA', cls: 'bg-secondary' }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Review Details</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/reviews')}
                    style={{ padding: '8px 20px', fontSize: '14px', borderRadius: '6px' }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Summary Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4 d-flex align-items-center gap-4 flex-wrap">
                    <div
                        style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '12px',
                            backgroundColor: '#fff8e1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <i className="fe-star" style={{ fontSize: '28px', color: '#ffc107' }} />
                    </div>

                    <div>
                        <h5 className="mb-1 fw-semibold">Review #{id}</h5>
                        <p className="mb-2 text-muted" style={{ fontSize: '14px' }}>
                            Booking: <strong>{bookingR?.booking_id || 'NA'}</strong>
                        </p>
                        <div className="d-flex gap-3 flex-wrap align-items-center">
                            <StarRating rating={rating || 0} />
                            <span className={`badge ${is_active ? 'bg-success' : 'bg-secondary'}`}>
                                {is_active ? 'Active' : 'Inactive'}
                            </span>
                            {is_deleted === 1 && (
                                <span className="badge bg-danger">Deleted</span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Review Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Review Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Rating</small>
                                <StarRating rating={rating || 0} />
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Review</small>
                                <strong>{review || <span className="text-muted fst-italic">No review text provided</span>}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Booking Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Booking Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Booking ID</small>
                                <strong>{bookingR?.booking_id || 'NA'}</strong>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Booking Status</small>
                                <span className={`badge ${bookingBadge.cls}`}>{bookingBadge.label}</span>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Start Time</small>
                                <strong>
                                    {bookingR?.start_time
                                        ? moment(bookingR.start_time).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">End Time</small>
                                <strong>
                                    {bookingR?.end_time
                                        ? moment(bookingR.end_time).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tutor Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Tutor Information
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img
                                    src={tutorRating?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                                    alt="Tutor"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null
                                        e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                                    }}
                                    style={{
                                        width: '56px',
                                        height: '56px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #f8f9fa',
                                        flexShrink: 0,
                                    }}
                                />
                                <div>
                                    <div className="fw-semibold" style={{ fontSize: '15px' }}>
                                        {`${tutorRating?.first_name || ''} ${tutorRating?.last_name || ''}`.trim() || 'NA'}
                                    </div>
                                    {tutorRating?.is_premium_verified === 1 && (
                                        <span className="badge bg-warning text-dark mt-1" style={{ fontSize: '11px' }}>
                                            Premium Verified
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Email</small>
                                <strong>{tutorRating?.email || 'NA'}</strong>
                            </div>
                            {/* Rating Summary Button */}
                            <div style={{ paddingTop: '12px', borderTop: '1px solid #f1f3f5' }}>
                                <button
                                    className="btn btn-sm btn-outline-warning"
                                    onClick={() => navigate('/reviews/tutor-summary', { state: { tutorId: tutorRating?.id } })}
                                    style={{
                                        fontSize: '13px',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontWeight: '500',
                                    }}
                                >
                                    <i className="fe-bar-chart-2" style={{ fontSize: '14px' }} />
                                    View Rating Summary
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Parent Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Parent Information
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img
                                    src={process.env.PUBLIC_URL + '/assets/avatar.png'}
                                    alt="Parent"
                                    onError={(e) => {
                                        e.currentTarget.onerror = null
                                        e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                                    }}
                                    style={{
                                        width: '56px',
                                        height: '56px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #f8f9fa',
                                        flexShrink: 0,
                                    }}
                                />
                                <div className="fw-semibold" style={{ fontSize: '15px' }}>
                                    {`${parentRating?.first_name || ''} ${parentRating?.last_name || ''}`.trim() || 'NA'}
                                </div>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Email</small>
                                <strong>{parentRating?.email || 'NA'}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Record Timestamps */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Record Information
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <small className="text-muted d-block mb-1">Created At</small>
                                <strong>
                                    {createdAt
                                        ? moment(createdAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                            <div>
                                <small className="text-muted d-block mb-1">Last Updated</small>
                                <strong>
                                    {updatedAt
                                        ? moment(updatedAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ViewReviewDetail